import { type LucideIcon } from 'lucide-react';

import { cn } from '../../lib/cn';

/**
 * docs/02-DESIGN-SYSTEM.md §4.1 — "Wrapper over a Lucide-based set + custom
 * SVGs for the illustrated icons in `Assets/Icons/`."
 *
 * The wrapper exists to make the accessibility decision explicit at every call
 * site. An icon is one of two things and never both:
 *
 *   - decoration beside a text label → `aria-hidden`, contributing nothing
 *   - the only content of a control → it needs a `label`, which is
 *     user-visible text and comes from the i18n bundle (CLAUDE.md rule 2)
 *
 * Defaulting to decorative is the safe direction: a redundant announcement is
 * a nuisance, a missing one is a blocker (§7.4).
 *
 * The illustrated PNGs in `Assets/Icons/` are not Lucide glyphs; they are
 * content images and belong in the media library, rendered as `<img>` with
 * real alt text rather than through this component.
 */
export type IconSize = 'sm' | 'md' | 'lg';

const SIZES: Record<IconSize, number> = { sm: 16, md: 20, lg: 24 };

export interface IconProps {
  /** A Lucide icon component, e.g. `Search` from `lucide-react`. */
  as: LucideIcon;
  size?: IconSize;
  /**
   * Set only when the icon carries meaning on its own — an icon-only button,
   * for instance. Leaving it unset marks the icon decorative.
   */
  label?: string;
  className?: string;
}

export function Icon({ as: Glyph, size = 'md', label, className }: IconProps) {
  return (
    <Glyph
      size={SIZES[size]}
      className={cn('shrink-0', className)}
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
      aria-label={label}
      // Lucide's default 2px stroke is heavy next to Manrope at body sizes.
      strokeWidth={1.75}
      focusable="false"
    />
  );
}
